<template>
    <div
        :class="[
            props.icon ? 'with-icon' : '',
            props.isError ? 'with-error' : '',
            data.isOpenDropdown ? 'opened' : '',
            props.disabled ? 'disabled' : '',
            'autocomplete-field'
        ]"
    >
        <div class="trigge-box"
            v-if="slots.trigger"
            @click="toggleFieldOpened"
            ref="trigger"
        >
            <slot name="trigger"></slot>
        </div>

        <div v-else @click="toggleFieldOpened">
            <input
                autocomplete="off"
                ref="field"
                v-model="data.searchStr"
                :placeholder="props.placeholder"
                :disabled="props.disabled"
                :class="{
                    clearable: props.clearable && data.searchStr && !props.disabled,
                    'pointer-events-none': props.maxValuesCount === 1 && data.values.length === 1,
                    'without-placeholder': !props.placeholder
                }"
                @input="onInput"
                @keyup.enter="onInput"
            />

            <span class="placeholder" :title="props.placeholder">{{ props.placeholder }}</span>

            <Icon v-if="props.icon" :icon="props.icon" :sprite="props.sprite" />

            <div class="actions-block">
                <IconButton
                    v-if="props.clearable && data.searchStr && !props.disabled"
                    tabindex="-1"
                    icon="close" sprite="navigation"
                    class="input-action tertiary size-s"
                    @click.stop="clean"
                />

                <Icon icon="chevron-down" sprite="navigation" class="arrow-down" />
            </div>
        </div>

        <ModalBox v-if="data.isOpenDropdown"
            :field="slots.trigger ? trigger : field"
            :width="props.optionWidth"
            @close="toggleOpened"
            @onSwipeBottom="toggleOpened"
        >
            <ul>
                <li v-if="data.isMobile" class="box-input">
                    <Field
                        :icon="props.icon"
                        :sprite="props.sprite"
                        :placeholder="props.placeholder"
                        :value="data.searchStr"
                        :autofocus="true"
                        class="noerror light with-placeholder"
                        @change="onBoxInput"
                    />
                </li>
                <li
                    v-for="option in props.optionList"
                    :value="option.code"
                    :key="option.code"
                    @click.stop="onChoose($event, option)"
                    :class="{ selected: data.values.find(item => item.code === option.code) }"
                >
                    <slot :option="option" name="option">
                        {{ props.useI18n ? t(option.name) : option.name }}
                    </slot>

                    <Icon v-show="data.values.find(item => item.code === option.code)" icon="check" sprite="actions" class="check" />
                </li>
            </ul>
        </ModalBox>

        <span class="error">{{ props.error }}</span>

        <div>
            <ul class="values" v-if="props.maxValuesCount !== 1 && !!data.values.length">
                <li v-for="item in previewValues" :key="item.code">
                    <slot :value="item" name="value">
                        <span>{{ props.useI18n ? t(item.name) : item.name }}</span>
                    </slot>
                    <IconButton
                        tabindex="-1"
                        icon="close" sprite="navigation"
                        :disabled="props.disabled"
                        class="tertiary size-m"
                        @click.stop="removeValue(item)"
                    />
                </li>
                <li v-if="props.maxPreviewValuesCount && data.values?.length > props.maxPreviewValuesCount"
                    class="more"
                >
                    <span>{{ t('ui.autocomplete.more') }} <b>{{ data.values.length - props.maxPreviewValuesCount }}</b></span>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { computed, useSlots, reactive, ref, watch } from 'vue';
import IconButton from '🔗/components/ui/IconButton.vue';
import Field from '🔗/components/ui/Field.vue';
import ModalBox from '🔗/components/ui/ModalBox.vue';
import { isMobile } from '🔗/utils/utils.js';

const { t } = useI18n();
const field = ref(null);
const trigger = ref(null);
const slots = useSlots();

const props = defineProps({
    name: { type: String, default: '' },
    values: { type: Array, default: () => ([]) },
    icon: { type: String, default: '' },
    sprite: { type: String, default: '' },
    maxPreviewValuesCount: { type: [Number, null], default: null },
    maxValuesCount: { type: [Number, null], default: null },
    position: { type: Number, default: 0 },
    optionList: {
        type: Array,
        default: () => []
    },
    optionWidth: { type: [Number, null], default: null },
    placeholder: { type: String, default: '' },
    error: { type: String, default: '' },
    isError: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    useI18n: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true }
});

const data = reactive({
    values: props.values,
    searchStr: props.maxValuesCount === 1 && props.values.length === 1 ? 
        (props.useI18n ? t(props.values[0].name) : props.values[0].name) : '',
    isOpenDropdown: false,
    isMobile: isMobile()
});

const previewValues = computed(() => {
    if (!props.maxPreviewValuesCount) {
        return data.values;
    }

    return data.values.slice(0, props.maxPreviewValuesCount);
});

const emit = defineEmits(['change', 'search', 'clean']);

const clean = () => {
    if (field.value) {
        field.value.value = '';
    }
    data.searchStr = '';

    if (props.maxValuesCount && data.values.length === props.maxValuesCount) {
        data.values = [];
        emit('change', {
            name: props.name,
            values: [],
            position: props.position
        });
    }

    emit('search', '');
    emit('clean', '');
};

const toggleOpened = () => {
    if (props.disabled) {
        return;
    }

    data.isOpenDropdown = !data.isOpenDropdown;

    if (!data.isOpenDropdown) {
        return false;
    }
};

const toggleFieldOpened = () => {
    if (data.isOpenDropdown && data.isMobile) {
        return;
    }

    toggleOpened();
}

watch(() => props.values, (values) => {
    data.values = values || [];
});

const hide = () => (data.isOpenDropdown = false);

const onChoose = (event, payload) => {
    if (data.values.find(item => item.code === payload.code)) {
        data.values.splice(data.values.indexOf(data.values.find(item => item.code === payload.code)), 1);
    } else {
        if (props.maxValuesCount && data.values.length === props.maxValuesCount) {
            data.values.pop();
        }

        data.values.unshift(payload);
    }

    emit('change', {
        name: props.name,
        values: data.values,
        position: props.position
    });

    if (props.maxValuesCount === 1 && data.values.length === 1) {
        data.searchStr = props.useI18n ? t(data.values[0].name) : data.values[0].name;
        data.isOpenDropdown = false;
    }
};

const removeValue = (value) => {
    if (data.values.indexOf(value) === -1) {
        return;
    }

    data.values.splice(data.values.indexOf(value), 1);

    emit('change', {
        name: props.name,
        values: data.values,
        position: props.position
    });
}

const onInput = (evn) => {
    if (data.values.length && props.maxValuesCount === data.values.length) {
        return;
    }

    data.isOpenDropdown = true;
    emit('search', evn.target.value);
};

const onBoxInput = (evn) => {
    data.searchStr = evn.target.value;
    onInput(evn);
};

defineExpose({
    clean
});

</script>

<style lang="scss" scoped>
.autocomplete-field {
    display: flex;
    flex-direction: column;

    & > div {
        margin: 0 0 8px;
        display: flex;
        position: relative;
        cursor: pointer;
        width: 100%;

        &.trigge-box {
            margin: 0;
            &:first-child {
                max-width: none;
            }
        }

        &:first-child {
            max-width: var(--input-max-width);
        }

        .placeholder {
            @include input-placeholder;
        }

        .actions-block {
            position: absolute;
            top: 0;
            height: 100%;
            right: 8px;
            display: flex;
            align-items: center;

            & > svg {
                fill: var(--dark-80);
                stroke: var(--dark-80);
                height: 14px;
                width: 14px;
                margin: 0 9px;

                &.arrow-down {
                    transition: all 0.4s;
                }
            }
        }

        & > svg {
            &:not(.arrow-down) {
                position: absolute;
                left: 16px;
                top: 50%;
                transform: translateY(-50%);
                width: 20px;
                height: 20px;
                fill: var(--dark-80);
                stroke: var(--dark-80);
            }
        }

        input {
            @include input;
            padding-right: 44px;
            &.clearable {
                padding-right: 80px;
            }

            &.pointer-events-none {
                pointer-events: none;
            }
        }
    }

    &.disabled {
        & > div {
            cursor: default;
        }
    }

    &.opened {
        & > div {
            .arrow-down {
                transform: rotate(180deg);
            }

            input {
                border-color: var(--primary-500);
            }
        }
    }

    & > .error {
        font: var(--font-label);
        color: var(--secondary-500) !important;
        display: flex;
        min-height: 11px;
    }

    & > div > .values {
        @include values-list;
    }

    &.with-error {
        & > div {
            input {
                border-color: var(--secondary-500) !important;
            }

            svg,
            input:not(:placeholder-shown) + .placeholder + svg,
            input:focus + .placeholder + svg {
                fill: var(--secondary-500) !important;
                stroke: var(--secondary-80) !important;
            }

            input:not(:placeholder-shown) + .placeholder + svg,
            svg {
                color: var(--secondary-500) !important;
            }
        }
    }

    &.with-icon {
        & > div {
            input {
                padding-left: 52px;
            }

            .placeholder {
                left: 53px;
            }
        }
    }

    &.light {
        & > div {
            input {
                padding-top: 8px;
                padding-right: 16px;
                padding-bottom: 8px;
                height: 38px;
                text-overflow: ellipsis;
            }

            svg:not(.arrow-down) {
                height: 18px;
                width: 18px;
            }

            input:not(:placeholder-shown) + .placeholder,
            input:focus + .placeholder {
                display: none;
            }
        }
    }

    &.with-placeholder {
        & > div {
            input:not(:placeholder-shown) + .placeholder,
            input:focus + .placeholder {
                display: flex;

                top: -6px;
                background: var(--white-500);
            }
        }
    }

    &.noerror {
        & > div {
            margin: 0;
        }
        & > .error {
            display: none;
        }
    }
}

.direction-rtl .autocomplete-field {
    & > div {
        .placeholder {
            left: auto;
            right: 20px;
        }

        .actions-block {
            right: auto;
            left: 8px;
        }

        & > svg {
            &:not(.arrow-down) {
                left: auto;
                right: 16px;
            }
        }

        input {
            padding-right: 20px;
            padding-left: 44px;
            &.clearable {
                padding-left: 80px;
                padding-right: 20px;
            }
        }
    }

    & > div > .values {
        li {
            padding: 0 12px 0 0;
        }
    }

    &.with-icon {
        & > div {
            input {
                padding-right: 52px;
                &:not(.clearable) {
                    padding-left: 20px;
                }
            }

            .placeholder {
                left: auto;
                right: 53px;
            }
        }
    }

    &.light {
        & > div {
            input {
                padding-left: 16px;
                padding-right: 20px;
            }
        }
    }
}
</style>
